import { useCallback, useMemo, useState } from 'react'
import { CalendarClock, Eye, Plus, RefreshCw, X } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { EmptyRow, ErpTable, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { FilterBar } from '@/shared/ui/filters'
import { useCan } from '@/shared/auth/session'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { listCoverageEvents, listLiveCoverage } from './api'
import { CoverageEventDialog } from './EventDialog'
import { CoverageEventDrawer } from './EventDrawer'
import {
  EVENT_STATUS_ORDER, EVENT_TYPE_ORDER,
  classificationLabel, eventStatusLabel, eventTypeLabel, isSensitive, placeOf,
} from './vocab'

const PAGE_SIZE = 50

export function CoverageEventsPage() {
  const canRead = useCan('coverage:read')
  const canCreate = useCan('coverage:create')
  const [params, setParams] = useSearchParams()
  const [creating, setCreating] = useState(false)
  /*
   * Seeded from the URL so `?open=<id>` lands on the event itself. The calendar links here
   * with a specific event, and without this it opened the diary and left somebody to find
   * the thing they had just clicked.
   */
  const [openId, setOpenId] = useState<string | null>(params.get('open'))
  const [tick, setTick] = useState(0)

  const status = params.get('status') ?? ''
  const eventType = params.get('eventType') ?? ''
  const search = params.get('search') ?? ''
  // The programme drawer links here with both of these. They must be supplied
  // TOGETHER — the server 400s on a bare `subjectRef`, because matching a uuid
  // across types is a bug waiting for a collision and a plan that ignores the
  // partial index.
  const subjectTypeCode = params.get('subjectTypeCode') ?? ''
  const subjectRef = params.get('subjectRef') ?? ''
  const page = Number(params.get('page') ?? '0')
  const debounced = useDebounced(search, 300)

  const set = useCallback((k: string, v: string) => {
    const sp = new URLSearchParams(params)
    if (v) sp.set(k, v); else sp.delete(k)
    // Any filter change invalidates the page number: page 3 of a different list
    // is a different set of rows, and the empty page that results reads as "no
    // results" for a filter that has plenty.
    if (k !== 'page') sp.delete('page')
    setParams(sp, { replace: true })
  }, [params, setParams])

  const { data, error, loading } = useLoad(
    () => listCoverageEvents(
      {
        status: status || undefined,
        eventType: eventType || undefined,
        search: debounced || undefined,
        subjectTypeCode: subjectTypeCode && subjectRef ? subjectTypeCode : undefined,
        subjectRef: subjectTypeCode && subjectRef ? subjectRef : undefined,
      },
      page, PAGE_SIZE,
    ),
    [status, eventType, debounced, subjectTypeCode, subjectRef, page, tick],
  )

  const rows = data?.content ?? []

  /**
   * The live set, because the LIST does not carry feeds.
   *
   * `CoverageEventService.list()` passes `List.of()` to avoid an N+1 across 200
   * rows, so `e.feeds` is empty on every row here and a count derived from it
   * would read 0 for an event with three cameras on air. `GET /coverage/live` is
   * one query the server already runs for the live board, and it answers the half
   * of the question that matters — which of these is happening right now.
   *
   * The TOTAL camera count is genuinely unavailable on this screen until the
   * backend batches feeds into the list, and it is rendered as "—" rather than
   * as a zero. A zero would be a claim.
   */
  const liveFeeds = useLoad(() => listLiveCoverage(), [tick])
  const liveByEvent = useMemo(() => {
    const m = new Map<string, { cameras: number; viewers: number }>()
    for (const f of liveFeeds.data ?? []) {
      const cur = m.get(f.eventId) ?? { cameras: 0, viewers: 0 }
      cur.cameras += 1
      cur.viewers += f.liveViewers ?? 0
      m.set(f.eventId, cur)
    }
    return m
  }, [liveFeeds.data])

  if (!canRead) return <Forbidden what="live coverage" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Coverage Diary"
        description="Everything on the ground that somebody might film. A camera hangs off an event."
        right={
          <div className="flex items-center gap-1.5">
            <button type="button" className="erp-btn" aria-label="Refresh"
              onClick={() => setTick((t) => t + 1)}>
              <RefreshCw className={loading ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            </button>
            {canCreate && (
              <button type="button" className="erp-btn erp-btn-primary" onClick={() => setCreating(true)}>
                <Plus className="h-3.5 w-3.5" /> New event
              </button>
            )}
          </div>
        }
      />

      <FilterBar>
        <input
          className="erp-input h-9 w-56"
          type="search"
          value={search}
          onChange={(e) => set('search', e.target.value)}
          placeholder="Title, venue or reference"
          aria-label="Search events"
        />
        <select className="erp-input h-9" value={status} aria-label="Status"
          onChange={(e) => set('status', e.target.value)}>
          <option value="">Any status</option>
          {EVENT_STATUS_ORDER.map((s) => <option key={s} value={s}>{eventStatusLabel(s)}</option>)}
        </select>
        <select className="erp-input h-9" value={eventType} aria-label="Type"
          onChange={(e) => set('eventType', e.target.value)}>
          <option value="">Any type</option>
          {EVENT_TYPE_ORDER.map((t) => <option key={t} value={t}>{eventTypeLabel(t)}</option>)}
        </select>
        {subjectTypeCode && subjectRef && (
          <span className="flex items-center gap-1.5 rounded-md bg-chocolate-soft/60 px-2 py-1 text-erp-xs text-chocolate">
            Attached to one {subjectTypeCode.toLowerCase()}
            <button type="button" aria-label="Clear the attachment filter"
              onClick={() => { const sp = new URLSearchParams(params); sp.delete('subjectTypeCode'); sp.delete('subjectRef'); sp.delete('page'); setParams(sp, { replace: true }) }}>
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
        {(status || eventType || search || subjectTypeCode) && (
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm text-chocolate"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            Clear filters
          </button>
        )}
      </FilterBar>

      <ErrorStrip message={error} />

      <ErpTable head={(
        <>
          <Th>Event</Th>
          <Th className="hidden md:table-cell">When</Th>
          <Th className="hidden lg:table-cell">Where</Th>
          <Th>Status</Th>
          <Th className="hidden sm:table-cell">On air</Th>
          <Th className="hidden xl:table-cell">Covers</Th>
        </>
      )}>
        {loading && rows.length === 0 ? <LoadingRows colSpan={6} label="Loading the diary…" />
          : rows.length === 0 ? (
            <EmptyRow
              colSpan={6}
              message={status || eventType || search || subjectTypeCode
                ? 'No events match this filter.'
                : 'Nothing on the diary yet. An event is a thing that happens on the ground; cameras hang off it.'}
            />
          ) : rows.map((e) => {
            const onAir = liveByEvent.get(e.id)
            const live = onAir?.cameras ?? 0
            return (
              <tr
                key={e.id}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => setOpenId(e.id)}
              >
                <Td>
                  {/*
                    The row click is a convenience; the BUTTON is the control. A
                    `<tr onClick>` with nothing focusable inside it makes the whole
                    diary unreachable without a mouse, and opening an event is the
                    only thing this screen does.
                  */}
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className="truncate text-left font-medium hover:text-chocolate"
                      onClick={(ev) => { ev.stopPropagation(); setOpenId(e.id) }}
                    >
                      {e.title}
                    </button>
                    {isSensitive(e.classificationCode) && (
                      <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-erp-xs font-medium text-amber-700 dark:text-amber-400">
                        {classificationLabel(e.classificationCode)}
                      </span>
                    )}
                  </div>
                  <div className="truncate text-erp-xs text-muted-foreground">
                    <span className="font-mono">{e.ref}</span> · {eventTypeLabel(e.eventTypeCode)}
                  </div>
                </Td>
                <Td className="hidden md:table-cell whitespace-nowrap">
                  <CalendarClock className="mr-1 inline h-3 w-3 text-muted-foreground" />
                  {new Date(e.scheduledStart).toLocaleString()}
                </Td>
                <Td className="hidden lg:table-cell">{placeOf(e)}</Td>
                <Td>
                  <span className={live > 0 ? 'font-medium text-red-600' : ''}>
                    {live > 0 ? 'Live' : eventStatusLabel(e.statusCode)}
                  </span>
                </Td>
                <Td className="hidden sm:table-cell tabular-nums">
                  {live > 0 ? (
                    <span className="inline-flex items-center gap-1 font-medium text-red-600">
                      {live} on air
                      <Eye className="h-3 w-3" />
                      {onAir?.viewers ?? 0}
                    </span>
                  ) : (
                    <span className="text-muted-foreground" title="The event list does not carry camera counts; open the event to see its cameras.">
                      —
                    </span>
                  )}
                </Td>
                <Td className="hidden xl:table-cell">
                  {e.subject
                    ? (e.subject.resolved
                      ? <span className="truncate">{e.subject.label}</span>
                      // The owning feature could not name it — the module may be
                      // switched off, or the record deleted. Show what we know
                      // rather than an empty cell that reads as "nothing attached".
                      : <span className="text-muted-foreground">{e.subject.subjectTypeCode} (unresolved)</span>)
                    : <span className="text-muted-foreground">—</span>}
                </Td>
              </tr>
            )
          })}
      </ErpTable>

      {data && data.totalElements > PAGE_SIZE && (
        <div className="flex items-center justify-between text-erp-xs text-muted-foreground">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.totalElements)} of {data.totalElements}
          </span>
          <div className="flex gap-1.5">
            <button type="button" className="erp-btn h-8" disabled={page === 0}
              onClick={() => set('page', String(page - 1))}>Previous</button>
            <button type="button" className="erp-btn h-8"
              disabled={(page + 1) * PAGE_SIZE >= data.totalElements}
              onClick={() => set('page', String(page + 1))}>Next</button>
          </div>
        </div>
      )}

      {creating && (
        <CoverageEventDialog
          onClose={() => setCreating(false)}
          onCreated={(e) => { setCreating(false); setTick((t) => t + 1); setOpenId(e.id) }}
        />
      )}

      {openId && (
        <CoverageEventDrawer
          eventId={openId}
          onClose={() => {
            setOpenId(null)
            // Drop ?open= as well, or a refresh reopens what somebody just closed.
            if (params.get('open')) {
              const next = new URLSearchParams(params)
              next.delete('open')
              setParams(next, { replace: true })
            }
          }}
          onChanged={() => setTick((t) => t + 1)}
        />
      )}
    </div>
  )
}
